"use client";
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface DeepLinkState {
  applicationId?: number;
  hiredStatus?: 'hired' | 'not-hired';
  redirectPath?: string;
  hasDeepLinkAction: boolean;
}

interface DeepLinkContextType {
  deepLinkState: DeepLinkState;
  setDeepLinkState: (state: DeepLinkState) => void;
  clearDeepLinkState: () => void;
  executeDeepLinkAction: () => Promise<void>;
}

const DeepLinkContext = createContext<DeepLinkContextType | undefined>(undefined);

export const useDeepLink = () => {
  const context = useContext(DeepLinkContext);
  if (context === undefined) {
    throw new Error('useDeepLink must be used within a DeepLinkProvider');
  }
  return context;
};

interface DeepLinkProviderProps {
  children: ReactNode;
}

export const DeepLinkProvider: React.FC<DeepLinkProviderProps> = ({ children }) => {
  const [deepLinkState, setDeepLinkState] = useState<DeepLinkState>({
    hasDeepLinkAction: false
  });

  const clearDeepLinkState = () => {
    setDeepLinkState({ hasDeepLinkAction: false });
  };

  const executeDeepLinkAction = async (): Promise<void> => {
    if (!deepLinkState.hasDeepLinkAction || !deepLinkState.applicationId) {
      return;
    }

    try {
      clearDeepLinkState();
    } catch (error) {
      console.error('Error executing deep link action:', error);
      clearDeepLinkState();
    }
  };

  return (
    <DeepLinkContext.Provider
      value={{
        deepLinkState,
        setDeepLinkState,
        clearDeepLinkState,
        executeDeepLinkAction
      }}
    >
      {children}
    </DeepLinkContext.Provider>
  );
};
